import { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ZButton } from "@/shared/components/button";
import CustomizeControlSearchBar from "./CustomizeControlSearchBar";
import CustomizeControlTopLeftSection from "./CustomizeControlTopLeftSection";
import CustomizeControlTopRIghtSection from "./CustomizeControlTopRIghtSection";
import { useNistCheckboxContext } from "../CustomizeControlsTable";
import { useCustomizeControlsStore } from "@/modules/agent/states/cusomize-controls";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { updateControlSetControls } from "@/modules/settings/requests";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";

// Control Set Edit Header Component
const ControlSetEditHeader = ({ controlSetId }: { controlSetId: string }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSaving, setIsSaving] = useState(false);
  const { dataState, changesMade } = useCustomizeControlsStore();
  const hasUnsavedChanges = changesMade > 0;
  const currentControlsUrl = searchParams.get('controlsUrl');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!currentControlsUrl) {
        throw new Error('No controls URL found');
      }

      // Step 1: Prepare controls data for upload
      const controlsToSave = dataState.map((control) => {
        // Clean up the control object for storage
        const cleanControl = { ...control };
        
        // Remove temporary properties that shouldn't be saved
        delete (cleanControl as any).new_question;
        delete (cleanControl as any).id; // Remove the id property added for table rendering
        
        return cleanControl;
      });

      // Step 2: Create blob with updated controls
      const controlsJson = JSON.stringify(controlsToSave);
      const blob = new Blob([controlsJson], { type: "application/json" });

      // Step 3: Get fresh signed URL and upload blob
      const validSignedUrl = await checkAndRefreshUrl(currentControlsUrl);
      await uploadFileReq(validSignedUrl, blob);

      // Step 4: Update control set via PATCH API
      const [error, response] = await parsePromise(
        updateControlSetControls(controlSetId, {
          controls_url: currentControlsUrl,
          operation_type: "update_control_set_controls",
        })
      );

      if (error) {
        throw error;
      }

      addNotification({
        type: "success",
        message: "Controls saved successfully.",
      });
      
      // Navigate back to settings
      navigate('/settings');
    } catch (error) {
      console.error('Error saving controls:', error);
      addNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to exit?')) {
        navigate('/settings');
      }
    } else {
      navigate('/settings');
    }
  };

  return (
    <div className="px-6 py-4 grid grid-cols-3 min-h-[69px] bg-white gap-2 border-b">
      {/* Left: Back arrow and title */}
      <div className="col-span-1 flex items-center">
        <div className="text-sm font-medium">
          Edit Control Set
          {hasUnsavedChanges && (
            <span className="ml-2 text-xs text-amber-600">• Unsaved changes</span>
          )}
        </div>
      </div>

      {/* Center: Empty for now */}
      <div className="col-span-1"></div>

      {/* Right: Save and Exit buttons */}
      <div className="col-span-1 flex justify-end items-center gap-2">
        <ZButton
          variant="bordered"
          className="text-xs font-medium h-7 px-3"
          onClick={handleExit}
          isDisabled={isSaving}
        >
          Exit
        </ZButton>
        <ZButton
          color="primary"
          className="text-xs font-medium h-7 px-3"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={isSaving}
        >
          Save
        </ZButton>
      </div>
    </div>
  );
};

const CustomizeControlTopSection = () => {
  // const { showCheckboxes } = useNistCheckboxContext();
  const {showCheckboxes, noOfGenerateWithAiPopoverOpens} = useCustomizeControlsStore();
  const { changesMade } = useCustomizeControlsStore();
  const [searchParams] = useSearchParams();

  // Check if we're in control set edit mode
  const isControlSetEdit = searchParams.get('isControlSetEdit') === 'true';
  const controlSetId = searchParams.get('controlSetId');

  // Force component to re-evaluate on changes
  const key = useMemo(() => `top-section-${changesMade}`, [changesMade]);

  // Render control set edit header if in edit mode
  if (isControlSetEdit && controlSetId) {
    return <ControlSetEditHeader controlSetId={controlSetId} />;
  }

  return (
    <div key={key} className="px-6 py-4 grid grid-cols-3 min-h-[69px] bg-white gap-2">
      <div className="col-span-1 items-center my-auto">
        <CustomizeControlTopLeftSection />
      </div>

      <div className="col-span-1">
      {/* Conditionally render search bar based on showCheckboxes and AI operations */}
      {!showCheckboxes && noOfGenerateWithAiPopoverOpens === 0 && (
          <CustomizeControlSearchBar />
        
      )}
      </div>
      <div className="col-span-1 flex justify-end items-center my-auto">
        <div className="flex flex-row gap-1">
          <CustomizeControlTopRIghtSection />
        </div>
      </div>
    </div>
  );
};

// Remove memo since it's causing rendering issues
export default CustomizeControlTopSection;
